import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Palette, 
  Droplets, 
  Scissors, 
  CircleDot,
  Layers,
  Ruler,
  Heart,
  Activity,
  FlaskConical
} from "lucide-react";

// Opzioni per le 10 domande
export const PROFILO_OPTIONS = {
  colore_naturale: [
    { value: "biondo", label: "Biondo" },
    { value: "castano", label: "Castano" },
    { value: "nero", label: "Nero" },
    { value: "rosso", label: "Rosso" },
    { value: "bianco_grigio", label: "Bianco/Grigio" }
  ],
  colore_attuale: [
    { value: "naturale", label: "Naturale" },
    { value: "colorato", label: "Colorato" },
    { value: "meches", label: "Mèches" },
    { value: "balayage", label: "Balayage" },
    { value: "decolorato", label: "Decolorato" }
  ],
  percentuale_bianchi: [
    { value: "0", label: "0%" },
    { value: "10-30", label: "10-30%" },
    { value: "30-50", label: "30-50%" },
    { value: "50-70", label: "50-70%" },
    { value: "70-100", label: "70-100%" }
  ],
  tipo_capello: [
    { value: "liscio", label: "Liscio" },
    { value: "mosso", label: "Mosso" },
    { value: "riccio", label: "Riccio" },
    { value: "molto_riccio", label: "Molto Riccio" }
  ],
  spessore: [
    { value: "sottile", label: "Sottile" },
    { value: "medio", label: "Medio" },
    { value: "grosso", label: "Grosso" }
  ],
  densita: [
    { value: "pochi", label: "Pochi" },
    { value: "normale", label: "Normale" },
    { value: "molti", label: "Molti" }
  ],
  lunghezza: [
    { value: "corti", label: "Corti" },
    { value: "medi", label: "Medi" },
    { value: "lunghi", label: "Lunghi" },
    { value: "molto_lunghi", label: "Molto Lunghi" }
  ],
  stato_capello: [
    { value: "sano", label: "Sano" },
    { value: "secco", label: "Secco" },
    { value: "danneggiato", label: "Danneggiato" },
    { value: "molto_danneggiato", label: "Molto Danneggiato" }
  ],
  cuoio_capelluto: [
    { value: "normale", label: "Normale" },
    { value: "grasso", label: "Grasso" },
    { value: "secco", label: "Secco" },
    { value: "sensibile", label: "Sensibile" },
    { value: "forfora", label: "Forfora" }
  ],
  trattamenti_chimici: [
    { value: "nessuno", label: "Nessuno" },
    { value: "colore", label: "Colore" },
    { value: "decolorazione", label: "Decolorazione" },
    { value: "permanente", label: "Permanente" },
    { value: "stiratura", label: "Stiratura" }
  ]
};

// Campi con icone e label
export const PROFILO_FIELDS = [
  { key: "colore_naturale", label: "Colore Naturale", icon: Palette, color: "text-amber-600" },
  { key: "colore_attuale", label: "Colore Attuale", icon: Palette, color: "text-purple-600" },
  { key: "percentuale_bianchi", label: "% Capelli Bianchi", icon: Droplets, color: "text-slate-500" },
  { key: "tipo_capello", label: "Tipo Capello", icon: Scissors, color: "text-blue-600" },
  { key: "spessore", label: "Spessore", icon: CircleDot, color: "text-green-600" },
  { key: "densita", label: "Densità", icon: Layers, color: "text-indigo-600" },
  { key: "lunghezza", label: "Lunghezza", icon: Ruler, color: "text-pink-600" },
  { key: "stato_capello", label: "Stato Capello", icon: Heart, color: "text-red-500" },
  { key: "cuoio_capelluto", label: "Cuoio Capelluto", icon: Activity, color: "text-teal-600" },
  { key: "trattamenti_chimici", label: "Trattamenti Chimici", icon: FlaskConical, color: "text-orange-600" }
];

export default function ProfiloCapelliForm({ profilo, onChange, compact = false }) {
  const handleChange = (campo, valore) => {
    onChange({
      ...profilo,
      [campo]: valore
    });
  };

  const gridClass = compact 
    ? "grid grid-cols-2 gap-3" 
    : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4";

  return (
    <div className={gridClass}>
      {PROFILO_FIELDS.map(({ key, label, icon: Icon, color }) => (
        <div key={key} className="space-y-1.5">
          <Label className="flex items-center gap-2 text-sm">
            <Icon className={`w-4 h-4 ${color}`} />
            {label}
          </Label>
          <Select
            value={profilo?.[key] || ""}
            onValueChange={(v) => handleChange(key, v)}
          >
            <SelectTrigger className={compact ? "h-9 text-sm" : "h-10"}>
              <SelectValue placeholder="Seleziona..." />
            </SelectTrigger>
            <SelectContent>
              {PROFILO_OPTIONS[key].map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

// Componente per visualizzare il profilo in sola lettura
export function ProfiloCapelliView({ profilo }) {
  if (!profilo || Object.values(profilo).every(v => !v || v === "")) {
    return (
      <p className="text-slate-400 italic text-sm">Nessun profilo capelli registrato</p>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {PROFILO_FIELDS.map(({ key, label, icon: Icon, color }) => {
        const valore = profilo?.[key];
        if (!valore) return null;
        
        const option = PROFILO_OPTIONS[key]?.find(o => o.value === valore);
        
        return (
          <div key={key} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
            <Icon className={`w-4 h-4 ${color} shrink-0`} />
            <div className="min-w-0">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
              <p className="text-sm font-medium truncate">{option?.label || valore}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
